import { NavLink, ThemeIcon, useComputedColorScheme } from "@mantine/core";
import { useLocation, type useMatchRoute } from "@tanstack/react-router";

import NavbarListItem from "@/components/Navbar/NavbarListItem";
import { type NavigationGroup } from "@/components/Navbar/useNavigationElements";
import { SYSTEM_COLORS } from "@/configs/colorScheme";
import { useIsMobile } from "@/hooks/useIsMobile";

import styles from "@/components/Navbar/NavbarListItem.module.css";

type MatchRoute = ReturnType<typeof useMatchRoute>;

interface NavbarListGroupItemProps {
  group: NavigationGroup;
  matchRoute: MatchRoute;
  onClose?: () => void;
}

export const NavbarListGroupItem = ({ group, matchRoute, onClose }: NavbarListGroupItemProps) => {
  const { isTablet } = useIsMobile();
  const colorScheme = useComputedColorScheme();
  const isDarkTheme = colorScheme === "dark";

  // Recompute active state inside a location selector so the group re-evaluates on client
  // navigation, not only on mount/reload — matchRoute alone does not trigger a re-render.
  const isActive = useLocation({
    select: () => group.items.some((item) => Boolean(matchRoute({ ...item.to, fuzzy: true }))),
  });
  return (
    <NavLink
      label={group.title}
      leftSection={
        <ThemeIcon
          size="sm"
          p={isActive ? 3 : undefined}
          autoContrast
          color={isActive ? SYSTEM_COLORS.secondary : undefined}
          variant={isActive ? (isDarkTheme ? "light" : "filled") : "transparent"}
        >
          {group.icon}
        </ThemeIcon>
      }
      autoContrast
      className={isTablet ? styles["custom-nav-link-mobile"] : styles["custom-nav-link"]}
      defaultOpened={isActive}
    >
      {group.items.map((navElementChild) => (
        <NavbarListItem key={navElementChild.title} navigationItem={navElementChild} onClose={onClose} />
      ))}
    </NavLink>
  );
};

export default NavbarListGroupItem;
